import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PersonCheckInApi } from "../../api/PersonCheckInApi";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { DeletePersonCheckInStaff } from "../../generated/dto/DeletePersonCheckInStaff";
import type { PersonCheckIn } from "../../generated/dto/PersonCheckIn";
import type { PersonCheckInCreatePersonCheckInWithLookupRequest } from "../../generated/dto/PersonCheckInCreatePersonCheckInWithLookupRequest";
import type { PersonCheckInFetchOptions } from "../../generated/dto/PersonCheckInFetchOptions";
import type { PersonCheckInSearchParams } from "../../generated/dto/PersonCheckInSearchParams";
import type { PersonCheckInStaff } from "../../generated/dto/PersonCheckInStaff";
import type { PersonCheckInStaffSearchResult } from "../../generated/dto/PersonCheckInStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PersonCheckInApiClient implements PersonCheckInApi {
  constructor(private readonly client = useClient()) {}

  async getPersonCheckInStaff(id: number, options?: PersonCheckInFetchOptions): Promise<PersonCheckInStaff> {
    const { data } = await this.client.get<unknown>(`/personcheckin/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PersonCheckInFetchOptions") });
    return deserializeCbiDto<PersonCheckInStaff>(data, "generated.dto.PersonCheckInStaff");
  }

  async deletePersonCheckInStaff(source: DeletePersonCheckInStaff): Promise<void> {
    await this.client.delete(`/personcheckin/staff/${source.assignId}`);
  }

  async searchPersonCheckInStaff(params: PersonCheckInSearchParams, page: number, perPage: number): Promise<PaginationResult<PersonCheckInStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/personcheckin/staff/search`, serializeCbiDto(params, "generated.dto.PersonCheckInSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PersonCheckInStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PersonCheckInStaffSearchResult>>(result.items, "generated.dto.PersonCheckInStaffSearchResult") };
    return result;
  }

  async searchPersonCheckInStaffFull(params: PersonCheckInSearchParams, page: number, perPage: number, options?: PersonCheckInFetchOptions): Promise<PaginationResult<PersonCheckInStaff>> {
    const { data } = await this.client.post<unknown>(`/personcheckin/staff/search/full`, serializeCbiDto(params, "generated.dto.PersonCheckInSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.PersonCheckInFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<PersonCheckInStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PersonCheckInStaff>>(result.items, "generated.dto.PersonCheckInStaff") };
    return result;
  }

  async getPersonCheckInStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/personcheckin/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getPersonCheckInStaffCardId(id: number, options?: CardFetchOptions): Promise<CardStaff | null> {
    const { data } = await this.client.get<unknown>(`/personcheckin/staff/${id}/cardid`, { params: serializeCbiDto(options, "generated.dto.CardFetchOptions") });
    return deserializeCbiDto<CardStaff | null>(data, "generated.dto.CardStaff");
  }

  async createPersonCheckInWithLookupStaff(request: PersonCheckInCreatePersonCheckInWithLookupRequest): Promise<PersonCheckIn> {
    const { data } = await this.client.post<unknown>(`/personcheckin/staff/createpersoncheckinwithlookup`, serializeCbiDto(request, "generated.dto.PersonCheckInCreatePersonCheckInWithLookupRequest"));
    let result = data as PersonCheckIn;
    result = deserializeCbiDto<PersonCheckIn>(result, "generated.dto.PersonCheckIn");
    return result;
  }
}

export const usePersonCheckInApi = (): PersonCheckInApi => new PersonCheckInApiClient();
